/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import dayjs, {Dayjs} from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import isBetween from 'dayjs/plugin/isBetween'
import toObject from 'dayjs/plugin/toObject'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/cs'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/nl'
import 'dayjs/locale/hu'
import 'dayjs/locale/sk'
import {LOCALE} from 'helpers/config'
import {range} from 'utils/utility'
import {DATE_FORMATS} from 'constants/DateFormats'


interface DateTimeFormatOptions {
    localeMatcher?: 'best fit' | 'lookup' | undefined
    weekday?: 'long' | 'short' | 'narrow' | undefined
    era?: 'long' | 'short' | 'narrow' | undefined
    year?: 'numeric' | '2-digit' | undefined
    month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow' | undefined
    day?: 'numeric' | '2-digit' | undefined
    hour?: 'numeric' | '2-digit' | undefined
    minute?: 'numeric' | '2-digit' | undefined
    second?: 'numeric' | '2-digit' | undefined
    timeZoneName?:
        | 'short'
        | 'long'
        | 'shortOffset'
        | 'longOffset'
        | 'shortGeneric'
        | 'longGeneric'
        | undefined
    formatMatcher?: 'best fit' | 'basic' | undefined
    hour12?: boolean | undefined
    timeZone?: string | undefined
}

interface Date {}

type DayJsDate = dayjs.ConfigType

export interface YearMonth {
    year: number
    month: number
}

class Dates {
    constructor() {
        dayjs.extend(weekday)
        dayjs.extend(isBetween)
        dayjs.extend(toObject)
        dayjs.extend(customParseFormat)
        dayjs.locale('de-CH')
    }

    date = (
        date?: DayJsDate,
        defaultValue?: string,
        format?: string
    ): string => {
        if (!this.dayjs(date).isValid()) {
            return (date as string) || defaultValue || ''
        }
        return this.dayjs(date).format(format || DATE_FORMATS.DATE)
    }

    ymd = (date?: DayJsDate, format?: string): string => {
        return this.date(date, undefined, format ?? DATE_FORMATS.DATE_YMD)
    }

    time = (date?: DayJsDate): string | null => {
        if (!this.dayjs(date).isValid()) {
            return null
        }
        return this.dayjs(date).format(DATE_FORMATS.TIME)
    }

    hour = (date?: DayJsDate): number => {
        return parseInt(this.dayjs(date).format('H'))
    }

    minute = (date?: DayJsDate): number => {
        return parseInt(this.dayjs(date).format('mm'))
    }

    hoursDifference = (date1: DayJsDate, date2: DayJsDate) => {
        const dateObj = this.dayjs(date1)
        if (!dateObj.isValid()) {
            return null
        }
        if (dateObj.diff(date2, 'hour') < 0) {
            return ''
        }
        const hourDiff =
            dateObj.diff(date2, 'hour') - dateObj.diff(date2, 'day') * 24
        const minDiff =
            dateObj.diff(date2, 'minute') - dateObj.diff(date2, 'hour') * 60
        return (
            (hourDiff < 10 ? '0' + hourDiff : hourDiff) +
            ':' +
            (minDiff < 10 ? '0' + minDiff : minDiff)
        )
    }

    daysDifference = (date1: DayJsDate, date2: DayJsDate): number | null => {
        if (!date2) {
            date2 = this.dayjs()
        }
        const dateObj = this.dayjs(date1)
        if (!dateObj.isValid()) {
            return null
        }
        return dateObj.diff(date2, 'day', true)
    }

    timesDifference = (date1: DayJsDate, date2: DayJsDate): number => {
        const dateObj = this.dayjs(date1)
        const dateObj2 = this.dayjs(date2)
        if (!dateObj.isValid() || !dateObj2.isValid()) {
            return 0
        }
        return dateObj.diff(dateObj2, 'minute')
    }

    daysInText = (days: string) => {
        const daysInt = Math.abs(parseInt(days))
        if (daysInt === 0) {
            return ' - today'
        }
        if (daysInt === 1) {
            return 'day'
        }
        if (daysInt >= 2 && daysInt <= 4) {
            return 'days'
        }
        if (daysInt >= 5) {
            return 'days'
        }
        return '?'
    }

    daysDifferenceAsText = (date: DayJsDate, date2: DayJsDate): string => {
        const daysDiff = this.daysDifference(date, date2)
        if (daysDiff === null) {
            return '?'
        }

        if (
            daysDiff > 0 &&
            daysDiff < 1 &&
            this.date(date) !== this.date(date2)
        ) {
            return 'Tomorrow'
        }

        if (Math.floor(daysDiff) === 0) {
            return 'Today'
        }

        const daysDiffText = daysDiff >= 0 ? '+' + daysDiff : '' + daysDiff

        return (
            Math.floor(daysDiff) +
            ' ' +
            this.daysInText(daysDiffText) +
            ' ' +
            this.hoursDifference(date, date2)
        )
    }

    dayjs = (date?: DayJsDate): Dayjs => {
        let dateObj = dayjs(date)

        if (typeof date === 'number') {
            dateObj = dayjs.unix(date)
        }
        return dateObj
    }

    dayNameTranslated(dayName: string): string {
        const days: Record<string, string> = {
            monday: 'Montag',
            tuesday: 'Dienstag',
            wednesday: 'Mittwoch',
            thursday: 'Donnerstag',
            friday: 'Freitag',
            saturday: 'Samstag',
            sunday: 'Sonntag',
        }
        return days[dayName.toLowerCase()] || dayName
    }

    timeWithoutZero(open: string) {
        return dayjs(open, 'HH:mm').format('H:mm')
    }

    isToday = (date: globalThis.Date | string | number): boolean => {
        const today = dayjs()
        const d = dayjs(date)
        return d.isSame(today, 'day')
    }

    weekDay = (date: any): number => {
        return dayjs(date).day()
    }

    weekdayNameTranslated(dayNumber: number): string {
        const days: Record<number, string> = {
            1: 'Monday',
            2: 'Tuesday',
            3: 'Wednesday',
            4: 'Thursday',
            5: 'Friday',
            6: 'Saturday',
            0: 'Sunday',
        }
        return days[dayNumber] || '?'
    }

    addDays = (date: string | number, days: number): Date => {
        const result = new Date(date)
        result.setDate(result.getDate() + days)
        return result
    }

    intlDateFormat = (
        timestamp: number,
        options:
            | DateTimeFormatOptions
            | {
                  dateStyle: 'long' | 'short' | 'full' | 'medium' | undefined
              },
        locale: string = LOCALE
    ): string => {
        return Intl.DateTimeFormat(locale, options).format(timestamp)
    }

    dateTime = (
        date?: DayJsDate,
        defaultValue?: string,
        _format?: string
    ): string => {
        if (date === 0) {
            return defaultValue || '?'
        }

        if (!this.dayjs(date).isValid()) {
            return (date as string) || defaultValue || ''
        }
        return this.date(date, defaultValue, DATE_FORMATS.DATE_TIME)
    }

    generateYearMonth = (year: number): YearMonth[] => {
        const months = range(12, 1)
        return months.map((month) => {
            return { year: year, month: month }
        })
    }

    padTime(time: number): string {
        // return (time < 10 && "0") + time;
        return time < 10 ? '0' + time : time.toString()
    }

    durationHours = (seconds: number): string => {
        const h = Math.floor(seconds / 3600)
        const m = (seconds % 3600) / 60
        return this.padTime(h) + ':' + this.padTime(m)
    }

    getStartAndEndOfWeek = (date = new Date()) => {
        const day = date.getDay()
        const diffToMonday = day === 0 ? -6 : 1 - day

        const startOfWeek = new Date(date.getTime())
        startOfWeek.setDate(date.getDate() + diffToMonday)

        const endOfWeek = new Date(startOfWeek.getTime())
        endOfWeek.setDate(startOfWeek.getDate() + 6)

        const formatDate = (d: globalThis.Date) => {
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        return {
            startOfWeek: formatDate(startOfWeek),
            endOfWeek: formatDate(endOfWeek),
        }
    }

    getFirstDayOfWeekByDirection = (date = new Date(), direction = 'next') => {
        const currentDate = new Date(date)
        const day = currentDate.getDay()

        const diffToMonday = (day === 0 ? -6 : 1) - day
        const weekAdjustment =
            direction === 'next' ? 7 : direction === 'previous' ? -7 : 0

        currentDate.setDate(
            currentDate.getDate() + diffToMonday + weekAdjustment
        )
        currentDate.setHours(0, 0, 0, 0)
        return currentDate
    }

    dateFormatter = new Intl.DateTimeFormat(LOCALE, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })

    timeFormatter = new Intl.DateTimeFormat(LOCALE, {
        hour: 'numeric',
        minute: 'numeric',
    })
}

export default new Dates()
